/**
 * @file: 组件粘贴
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../cubeTree/index';
import * as UIToolkit from '../../UIToolkit/index';
import * as Toolkit from '../../toolkit/index';
import { StageEvent } from '../../message/index';
import UUID from '../../utils/uuid';
import Utils from '../../utils/util';
import Clipboard from '../../clipboard/index';
import NextTick from '../../utils/nextTick';
import Service from '../../service/index';
import Update from '../../update/index';
import Add from '../add/index';
import History from '../history/index';
import Select from '../select/index';
import Config from '../../config/index';
import { makeAddTaskQueue, isDiffBusiness } from './toolkit';
import { updateAllShelfsClass } from '../../update/updateAllShelfsClass';
import layerGroup from '../layerGroup';

interface CubeDataListTemp {
    cubeDataList: Array<CubeTreeData>,
    cubeDataMap: FreeObjMap
}

let pasting: boolean = false;

async function paste(): Promise<void> {
    if (pasting) {
        // 粘贴中
        UIToolkit.tips('粘贴中, 请稍后...');
        Toolkit.log({
            action: '粘贴',
            msgList: [
                {
                    value: '上一次粘贴操作未结束'
                }
            ]
        });
        return;
    }

    let clipboardData: Array<FreeObjMap> = await Clipboard.get({
        type: 'cube'
    });

    // 剪切板无数据
    if (!clipboardData.length) {
        Toolkit.log({
            action: '粘贴',
            msgList: [
                {
                    value: '剪切板无数据'
                }
            ]
        });
        return;
    }

    // 粘贴中
    pasting = true;
    UIToolkit.showLoading();
    Toolkit.log({
        action: '粘贴',
        msgList: [
            {
                name: '剪切板有数据, 开始执行粘贴操作, 剪切板数据:',
                value: clipboardData
            }
        ]
    });

    try {
        // 如果跨商家，需要把粘贴数据交给服务端，做下数据处理
        if (isDiffBusiness(clipboardData[0].extendParams.venderId)) {
            Toolkit.log({
                action: '粘贴',
                msgList: [
                    {
                        value: '跨商家粘贴'
                    }
                ]
            });
            await updateClipboardData(clipboardData);
            Toolkit.log({
                action: '粘贴',
                msgList: [
                    {
                        name: '剪切板数据被服务端处理后:',
                        value: clipboardData
                    }
                ]
            });
        }
        let addTaskQueue: Array<AddTask> = makeAddTaskQueue(clipboardData[0]);

        if (!addTaskQueue.length) {
            // 粘贴完成
            pasting = false;
            UIToolkit.hideLoading();
            Toolkit.log({
                action: '粘贴',
                msgList: [
                    {
                        value: '没有粘贴内容，提前结束粘贴任务'
                    }
                ]
            });
            return;
        }

        let addCubes: Array<CubeId> = await Add.runAddTaskQueue(addTaskQueue);

        if (CubeTree.cubeListHasShelf(addCubes)) {
            updateAllShelfsClass();
            // 对粘贴完成的新货架中的层板进行链接操作
            layerGroup.groupPasteShelf(addCubes, clipboardData[0]);
        }
        Select.addGroup(addCubes);
        Select.panelSelectAsync();
        History.stackIn({
            type: 'add',
            data: addCubes
        });
    }
    catch(err) {
        Toolkit.log({
            action: '粘贴',
            msgList: [
                {
                    name: '粘贴出错, 错误信息:',
                    value: err
                }
            ]
        });
    }

    // 粘贴完成
    pasting = false;
    UIToolkit.hideLoading();
    Toolkit.log({
        action: '粘贴',
        msgList: [
            {
                value: '粘贴完成'
            }
        ]
    });
}

async function updateClipboardData(clipboardData: FreeObjMap): Promise<void> {
    let venderIdNow: number = Config.get({
        moduleName: 'base',
        key: 'venderId'
    });
    let cubeDataListTemp: CubeDataListTemp = makeCubeDataListTemp(clipboardData[0].data);
    let checkResultList: Array<CubeTreeData> = [];

    if (typeof isMochaSys !== 'undefined') {
        // 用于单测时，模拟返回数据, diffBusinessPasteCheckTest 函数申明在单测环境中，不在src中
        checkResultList = diffBusinessPasteCheckTest(cubeDataListTemp.cubeDataList);
    }
    else {
        checkResultList = await Service.diffBusinessPasteCheck({
            pasteData: JSON.stringify(cubeDataListTemp.cubeDataList),
            pasteDataVenderId: clipboardData[0].extendParams.venderId,
            targetVenderId: venderIdNow
        });
    }

    // checkResultList 可能为空数组，服务端会剔掉不正确的数据，这时需要剪切板数据也要删除不需要的数据
    clipboardData[0].data = updateCubeDataListTemp(checkResultList, cubeDataListTemp);
}

function makeCubeDataListTemp(cubeList: Array<CubeCopyStoreData>): CubeDataListTemp {
    let result: CubeDataListTemp = {
        cubeDataList: [],
        cubeDataMap: {}
    };

    // cubeData.cubeCopyData  cubeData.extendParams
    cubeList.forEach((cubeData: CubeCopyStoreData) => {
        cubeData.cubeCopyData.id = new UUID().id;
        result.cubeDataList.push(cubeData.cubeCopyData);
        result.cubeDataMap[cubeData.cubeCopyData.id] = cubeData;
    });

    return result;
}

function updateCubeDataListTemp(checkedCubeDataList: Array<CubeTreeData>, cubeDataListTemp: CubeDataListTemp): Array<CubeCopyStoreData> {
    let newList: Array<CubeCopyStoreData> = [];

    checkedCubeDataList.forEach((cube: CubeTreeData) => {
        let id: CubeId = cube.id;

        delete cube.id;
        cubeDataListTemp.cubeDataMap[id].cubeCopyData = Utils.deepCopy(cube);
        newList.push(cubeDataListTemp.cubeDataMap[id]);
    });

    return newList;
}

export {
    paste
};
